import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
import filtersMeta from './filtersMeta.js';
import { useAppConfig } from '@state';
import { useDebounce, useSearchParams } from '@hooks';
import { hotkeys } from '@ohif/core';
import Studies from '../studies/studies';
import { StudyListFilter, useModal, AboutModal, UserPreferences, useSessionStorage, InvestigationalUseDialog } from '@ohif/ui';
import i18n from '@ohif/i18n';
import { signOut } from 'aws-amplify/auth';
import { useStudiesData } from '../../providers/studies/studiesConfig';

const availableLanguages = (i18n as any).availableLanguages || [];
const defaultLanguage = (i18n as any).defaultLanguage || i18n.language || 'en';
const currentLanguage = () => ({ value: i18n.language || defaultLanguage });


function WorkList({
    data: studies,
    dataTotal: studiesTotal,
    dataSource,
    hotkeysManager,
    onRefresh,
    servicesManager,
}: withAppTypes) {
    const { allStudies } = useStudiesData();
    const { hotkeyDefinitions, hotkeyDefaults } = hotkeysManager;
    const { show, hide } = useModal();
    const { t } = useTranslation();
    // ~ Modes
    const [appConfig] = useAppConfig();
    // ~ Filters
    const searchParams = useSearchParams();

    const STUDIES_LIMIT = 101;
    const queryFilterValues = _getQueryFilterValues(searchParams);
    const [updateSessionQueryFilterValues] = useSessionStorage({
        key: 'queryFilterValues',
        defaultValue: queryFilterValues,
        // ToDo: useSessionStorage currently uses an unload listener to clear the filters from session storage
        // so on systems that do not support unload events a, user will NOT be able to alter any existing filter
        // in the URL, load the page and have it apply.
        clearOnUnload: true,
    });
    const [filterValues, _setFilterValues] = useState({
        ...defaultFilterValues,
        // ...sessionQueryFilterValues,
    });

    const debouncedFilterValues = useDebounce(filterValues, 200);
    const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

    /*
     * The default sort value keep the filters synchronized with runtime conditional sorting
     * Only applied if no other sorting is specified and there are less than 101 studies
     */

    const canSort = studiesTotal < STUDIES_LIMIT;
    const shouldUseDefaultSort = sortBy === '' || !sortBy;
    const sortModifier = sortDirection === 'descending' ? 1 : -1;
    const defaultSortValues =
        shouldUseDefaultSort && canSort ? { sortBy: 'studyDate', sortDirection: 'ascending' } : {};

    if (canSort) {
        studies.sort((s1, s2) => {
            if (shouldUseDefaultSort) {
                const ascendingSortModifier = -1;
                return _sortStringDates(s1, s2, ascendingSortModifier);
            }

            const s1Prop = s1[sortBy];
            const s2Prop = s2[sortBy];

            if (typeof s1Prop === 'string' && typeof s2Prop === 'string') {
                return s1Prop.localeCompare(s2Prop) * sortModifier;
            } else if (typeof s1Prop === 'number' && typeof s2Prop === 'number') {
                return (s1Prop > s2Prop ? 1 : -1) * sortModifier;
            } else if (!s1Prop && s2Prop) {
                return -1 * sortModifier;
            } else if (!s2Prop && s1Prop) {
                return 1 * sortModifier;
            } else if (sortBy === 'studyDate') {
                return _sortStringDates(s1, s2, sortModifier);
            }

            return 0;
        });
    }

    // ~ Rows & Studies
    const [expandedRows, setExpandedRows] = useState([]);
    const numOfStudies = allStudies?.length || 0;

    const setFilterValues = val => {
        if (filterValues.pageNumber === val.pageNumber) {
            val.pageNumber = 1;
        }
        _setFilterValues(val);
        updateSessionQueryFilterValues(val);
        setExpandedRows([]);
    };
    useEffect(() => {
        if (!debouncedFilterValues) {
            return;
        }

        const queryString: Record<string, any> = {};
        Object.keys(defaultFilterValues).forEach(key => {
            const defaultValue = defaultFilterValues[key];
            const currValue = debouncedFilterValues[key];

            // TODO: nesting/recursion?
            if (key === 'studyDate') {
                if (currValue.startDate && defaultValue.startDate !== currValue.startDate) {
                    queryString.startDate = currValue.startDate;
                }
                if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
                    queryString.endDate = currValue.endDate;
                }
            } else if (key === 'modalities' && currValue.length) {
                queryString.modalities = currValue.join(',');
            } else if (currValue !== defaultValue) {
                queryString[key] = currValue;
            }
        });
    }, [debouncedFilterValues]);

    useEffect(() => {
    }, [expandedRows, studies]);

    const isFiltering = (filterValues, defaultFilterValues) => {
        return !isEqual(filterValues, defaultFilterValues);
    };

    const versionNumber = process.env.VERSION_NUMBER;
    const commitHash = process.env.COMMIT_HASH;

    const menuOptions = [
        {
            title: t('Header:About'),
            icon: 'info',
            onClick: () =>
                show({
                    content: AboutModal,
                    title: t('AboutModal:About OHIF Viewer'),
                    contentProps: { versionNumber, commitHash },
                    containerDimensions: 'max-w-4xl max-h-4xl',
                }),
        },
        {
            title: t('Header:Preferences'),
            icon: 'settings',
            onClick: () =>
                show({
                    title: t('UserPreferencesModal:User preferences'),
                    content: UserPreferences,
                    contentProps: {
                        hotkeyDefaults: hotkeysManager.getValidHotkeyDefinitions(hotkeyDefaults),
                        hotkeyDefinitions,
                        onCancel: hide,
                        currentLanguage: currentLanguage(),
                        availableLanguages,
                        defaultLanguage,
                        onSubmit: state => {
                            if (state.language.value !== currentLanguage().value) {
                                i18n.changeLanguage(state.language.value);
                            }
                            hotkeysManager.setHotkeys(state.hotkeyDefinitions);
                            hide();
                        },
                        onReset: () => hotkeysManager.restoreDefaultBindings(),
                        hotkeysModule: hotkeys,
                    },
                }),
        },
        {
            title: 'Log Out',
            icon: 'power-off',
            onClick: () => handleSignOut(),
        },
    ];

    if (appConfig.oidc) {
        menuOptions.push({
            icon: 'power-off',
            title: t('Header:Logout'),
            onClick: () => { },
        });
    }

    const { customizationService } = servicesManager.services;
    const dicomUploadComponent = customizationService.get('dicomUploadComponent')?.component as any;
    const uploadProps =
        dicomUploadComponent && dataSource.getConfig()?.dicomUploadEnabled
            ? {
                title: 'Upload files',
                closeButton: true,
                shouldCloseOnEsc: false,
                shouldCloseOnOverlayClick: false,
                content: () =>
                    dicomUploadComponent({
                        dataSource,
                        onComplete: () => {
                            hide();
                            onRefresh();
                        },
                        onStarted: () => {
                            show({
                                ...uploadProps,
                                // when upload starts, hide the default close button as closing the dialogue must be handled by the upload dialogue itself
                                closeButton: false,
                            });
                        },
                    }),
            }
            : undefined;

    const dataSourceConfigurationComponent =
        (customizationService.get('ohif.dataSourceConfigurationComponent') as any)?.component;

    return (
        < >
            <InvestigationalUseDialog dialogConfiguration={appConfig?.investigationalUseDialog} />

            <StudyListFilter
                numOfStudies={pageNumber * resultsPerPage > 100 ? 101 : numOfStudies}
                filtersMeta={filtersMeta}
                filterValues={{ ...filterValues, ...defaultSortValues }}
                onChange={setFilterValues}
                clearFilters={() => setFilterValues(defaultFilterValues)}
                isFiltering={isFiltering(filterValues, defaultFilterValues)}
                onUploadClick={uploadProps ? () => show(uploadProps) : undefined}
                getDataSourceConfigurationComponent={
                    dataSourceConfigurationComponent ? () => dataSourceConfigurationComponent() : undefined
                }
            />
            <Studies />
        </>
    );
}

WorkList.propTypes = {
    data: PropTypes.array.isRequired,
    dataSource: PropTypes.shape({
        query: PropTypes.object.isRequired,
        getConfig: PropTypes.func,
    }).isRequired,
    isLoadingData: PropTypes.bool.isRequired,
    servicesManager: PropTypes.object.isRequired,
};

const defaultFilterValues = {
    patientName: '',
    // mrn: '',
    studyDate: {
        startDate: null,
        endDate: null,
    },
    description: '',
    // modalities: [],
    accession: '',
    sortBy: '',
    sortDirection: 'none',
    pageNumber: 1,
    resultsPerPage: 25,
    datasources: '',
    configUrl: null,
};

function _tryParseInt(str, defaultValue) {
    let retValue = defaultValue;
    if (str && str.length > 0) {
        if (!isNaN(str)) {
            retValue = parseInt(str);
        }
    }
    return retValue;
}
async function handleSignOut() {
    try {
        await signOut();
    } catch (error) {
        console.log('error signing out: ', error);
    }
}

function _getQueryFilterValues(params) {
    const queryFilterValues = {
        patientName: params.get('patientname'),
        mrn: params.get('mrn'),
        studyDate: {
            startDate: params.get('startdate') || null,
            endDate: params.get('enddate') || null,
        },
        description: params.get('description'),
        modalities: params.get('modalities') ? params.get('modalities').split(',') : [],
        accession: params.get('accession'),
        sortBy: params.get('sortby'),
        sortDirection: params.get('sortdirection'),
        pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
        resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
        datasources: params.get('datasources'),
        configUrl: params.get('configurl'),
    };

    // Delete null/undefined keys
    Object.keys(queryFilterValues).forEach(
        key => queryFilterValues[key] == null && delete queryFilterValues[key]
    );

    return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
    // TODO: Delimiters are non-standard. Should we support them?
    const s1Date = moment(s1.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);
    const s2Date = moment(s2.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);

    if (s1Date.isValid() && s2Date.isValid()) {
        return (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier;
    } else if (s1Date.isValid()) {
        return sortModifier;
    } else if (s2Date.isValid()) {
        return -1 * sortModifier;
    }
}

export default WorkList;
